import { getUnprefixedLocalStorageItem, removeUnprefixedLocalStorageItem, setUnprefixedLocalStorageItem } from '@/utils/storage'

const apiBaseURL = 'https://test-api.siswe.com/api'?.replace(/\/+$/, '')

if (!apiBaseURL) {
  throw new Error('Missing VITE_API_BASE_URL for the current environment')
}

const ACCESS_TOKEN_KEY = 'chat3d_access_token'
const LEGACY_ACCESS_TOKEN_KEY = 'chat3d_token'
const REFRESH_TOKEN_KEY = 'chat3d_refresh_token'
const SHARED_ACCESS_TOKEN_KEY = 'siswe_access_token'
const SHARED_REFRESH_TOKEN_KEY = 'siswe_refresh_token'

interface ApiEnvelope<T> {
  code: number
  message: string
  data?: T
}

export interface TokenPair {
  accessToken: string
  refreshToken: string
  expiresIn: number
  sessionId: string
  userId: string
  isNewUser: boolean
  passwordSetupRequired: boolean
}

type ApiRequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
  auth?: boolean
  retryAuth?: boolean
  silent?: boolean
}

type SessionListener = (accessToken: string) => void

const sessionListeners = new Set<SessionListener>()
let refreshPromise: Promise<boolean> | null = null

export class ApiError extends Error {
  readonly status: number
  readonly code: number

  constructor(message: string, status = 0, code = 0) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

function emitSessionChange() {
  const accessToken = getAccessToken()
  sessionListeners.forEach(listener => listener(accessToken))
}

function readStoredAccessToken() {
  return getUnprefixedLocalStorageItem(SHARED_ACCESS_TOKEN_KEY)
    ?? getUnprefixedLocalStorageItem(ACCESS_TOKEN_KEY)
    ?? getUnprefixedLocalStorageItem(LEGACY_ACCESS_TOKEN_KEY)
    ?? ''
}

async function parseEnvelope<T>(response: Response): Promise<ApiEnvelope<T>> {
  const text = await response.text()
  if (!text) {
    return { code: response.ok ? 0 : response.status, message: response.statusText }
  }

  try {
    return JSON.parse(text) as ApiEnvelope<T>
  }
  catch {
    throw new ApiError('服务返回了无法解析的响应', response.status)
  }
}

async function refreshSession() {
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearSession()
      return false
    }

    try {
      const response = await fetch(`${apiBaseURL}/auth/v1/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      const envelope = await parseEnvelope<TokenPair>(response)
      if (!response.ok || envelope.code !== 0 || !envelope.data) {
        throw new ApiError(envelope.message || '登录状态已过期', response.status, envelope.code)
      }
      setSession(envelope.data)
      return true
    }
    catch {
      clearSession()
      return false
    }
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

export function getAccessToken() {
  return readStoredAccessToken()
}

export function getApiBaseURL() {
  return apiBaseURL
}

export function getRefreshToken() {
  return getUnprefixedLocalStorageItem(SHARED_REFRESH_TOKEN_KEY)
    ?? getUnprefixedLocalStorageItem(REFRESH_TOKEN_KEY)
    ?? ''
}

export function setSession(tokens: TokenPair) {
  setUnprefixedLocalStorageItem(SHARED_ACCESS_TOKEN_KEY, tokens.accessToken)
  setUnprefixedLocalStorageItem(SHARED_REFRESH_TOKEN_KEY, tokens.refreshToken)
  setUnprefixedLocalStorageItem(ACCESS_TOKEN_KEY, tokens.accessToken)
  removeUnprefixedLocalStorageItem(LEGACY_ACCESS_TOKEN_KEY)
  setUnprefixedLocalStorageItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
  emitSessionChange()
}

export function clearSession() {
  removeUnprefixedLocalStorageItem(SHARED_ACCESS_TOKEN_KEY)
  removeUnprefixedLocalStorageItem(SHARED_REFRESH_TOKEN_KEY)
  removeUnprefixedLocalStorageItem(ACCESS_TOKEN_KEY)
  removeUnprefixedLocalStorageItem(LEGACY_ACCESS_TOKEN_KEY)
  removeUnprefixedLocalStorageItem(REFRESH_TOKEN_KEY)
  emitSessionChange()
}

export function subscribeSession(listener: SessionListener) {
  sessionListeners.add(listener)
  return () => {
    sessionListeners.delete(listener)
  }
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { auth = true, retryAuth = true, silent = false, headers: suppliedHeaders, body, ...requestInit } = options
  const headers = new Headers(suppliedHeaders)
  const accessToken = getAccessToken()

  if (body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (auth && accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }

  let response: Response
  try {
    response = await fetch(`${apiBaseURL}${path}`, {
      ...requestInit,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }
  catch {
    throw new ApiError('无法连接后端服务')
  }

  const envelope = await parseEnvelope<T>(response)

  // Tokens issued before session-bound verification may not include the sessionId claim.
  // Some older gateway/identity combinations report this as 4001301 instead of 4091301.
  if (auth && retryAuth && [4091301, 4001301].includes(envelope.code)) {
    if (await refreshSession()) {
      return apiRequest<T>(path, { ...options, retryAuth: false })
    }
    throw new ApiError('登录状态已过期，请重新登录', 401, 4010001)
  }

  // Verification failures on authenticated security actions do not mean the session expired.
  if (response.status === 401 && auth && ![4011002, 4011101, 4011201, 4011301].includes(envelope.code)) {
    if (retryAuth && await refreshSession()) {
      return apiRequest<T>(path, { ...options, retryAuth: false })
    }
    clearSession()
    throw new ApiError('登录状态已过期，请重新登录', 401, 4010001)
  }

  if (!response.ok || envelope.code !== 0) {
    const message = envelope.message || `请求失败 (${response.status})`
    if (!silent) {
      useFaToast().error('Error', {
        description: message,
      })
    }
    throw new ApiError(message, response.status, envelope.code)
  }
  return envelope.data as T
}

export async function apiStream(path: string, options: Pick<RequestInit, 'headers' | 'signal'> = {}, retryAuth = true): Promise<Response> {
  const headers = new Headers(options.headers)
  const accessToken = getAccessToken()
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`)
  }
  headers.set('Accept', 'text/event-stream')

  let response: Response
  try {
    response = await fetch(`${apiBaseURL}${path}`, {
      method: 'GET',
      headers,
      signal: options.signal,
    })
  }
  catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }
    throw new ApiError('无法连接设备事件流')
  }

  if (response.status === 401 && retryAuth && await refreshSession()) {
    return apiStream(path, options, false)
  }
  if (response.status === 409 && retryAuth) {
    const envelope = await parseEnvelope<never>(response.clone())
    if (envelope.code === 4091301 && await refreshSession()) {
      return apiStream(path, options, false)
    }
  }
  if (!response.ok) {
    const envelope = await parseEnvelope<never>(response)
    throw new ApiError(envelope.message || `事件流连接失败 (${response.status})`, response.status, envelope.code)
  }
  return response
}
